import { assertNever } from "../../util/assertNever";
import {
  ArrayColumnOperators,
  BooleanColumnOperators,
  DateColumnOperators,
  NumberColumnOperators,
  StringColumnOperators,
} from "./enum.filterOperators";
import { GenericTableSchemaFormat } from "./enum.genericTableSchemaFormat";
import { QueryStringKeyword } from "./enum.queryStringKeyword";
import { filterMapperFactory, mapFieldDefinitionToFilterType } from "./filterMapper/factory.filterMapper";
import { FilterMapper } from "./filterMapper/interface.filterMapper";
import { GenericTableColumnDefinitions } from "./type.genericTableColumnDefinition";
import {
  FilterValueType,
  GenericTableFilterType,
  GenericTableQuery,
  GenericTableSearch,
  GenericTableSortingItems,
  SortingDirection,
} from "./type.genericTableQuery";
import { GenericTableSchema } from "./type.genericTableSchema";

export function generateUrlSearchParameters<T extends GenericTableSchema>(query: GenericTableQuery<T>) {
  const params = new URLSearchParams();
  generateFilterParameters({ params, filters: query.filters });
  generateSortingParameters({ params, sorting: query.sorting });
  generateSearchParameters({ params, search: query.search });
  generateSelectParameters({ params, columns: query.select?.map((column) => column.toString()) });
  query.pagination && generatePaginationParameters({ params, pagination: query.pagination });
  return params;
}

type GenerateFilterParametersParams = {
  params: URLSearchParams;
  filters: undefined | Record<string, FilterValueType>;
};
function generateFilterParameters({ params, filters }: GenerateFilterParametersParams) {
  if (!filters) return;

  for (const [field, fieldFilter] of Object.entries(filters)) {
    const filterMapper: FilterMapper = filterMapperFactory(fieldFilter.type);
    if (fieldFilter !== undefined && fieldFilter.filter.value !== undefined) {
      const query = filterMapper.toQuery(fieldFilter);
      if (query) {
        params.append(field, query);
      }
    }
  }
}

type GenerateSortingParametersParams = {
  params: URLSearchParams;
  sorting: undefined | GenericTableSortingItems;
};
function generateSortingParameters({ params, sorting }: GenerateSortingParametersParams) {
  if (!sorting) return;
  sorting.forEach((sortingItem) => {
    params.append(QueryStringKeyword.SORTING, `${sortingItem.fieldName}$-$${sortingItem.direction}`);
  });
}

type GenerateSearchParametersParams = {
  params: URLSearchParams;
  search: undefined | GenericTableSearch;
};
function generateSearchParameters({ params, search }: GenerateSearchParametersParams) {
  if (!search) return;
  if (search.searchTerm) {
    for (const column of search.searchColumns) {
      params.append(QueryStringKeyword.SEARCH_COLUMNS, column);
    }
    params.append(QueryStringKeyword.SEARCH_TERM, search.searchTerm);
  }
}

type GenerateSelectParametersParams = {
  params: URLSearchParams;
  columns: undefined | string[];
};
function generateSelectParameters({ params, columns }: GenerateSelectParametersParams) {
  if (columns && columns.length > 0) {
    for (const column of columns) {
      params.append(QueryStringKeyword.SELECT, column);
    }
  }
}

type GeneratePaginationParametersParams = {
  params: URLSearchParams;
  pagination: {
    currentPage: number;
    pageSize: number;
  };
};
function generatePaginationParameters({ params, pagination }: GeneratePaginationParametersParams) {
  params.append(QueryStringKeyword.PAGE, pagination.currentPage.toString());
  params.append(QueryStringKeyword.PAGE_SIZE, pagination.pageSize.toString());
}

type InitializeGenericTableQueryParams<T extends GenericTableSchema> = {
  genericTableSchema: T;
  genericTableColumnsDefinition: GenericTableColumnDefinitions<GenericTableSchema>;
  defaultValues: URLSearchParams;
};
export function initializeGenericTableQuery<T extends GenericTableSchema>({
  genericTableSchema,
  genericTableColumnsDefinition,
  defaultValues,
}: InitializeGenericTableQueryParams<T>): GenericTableQuery<T> {
  const query: GenericTableQuery<T> = {
    filters: initializeFilters({ genericTableColumnsDefinition, defaultValues, genericTableSchema }),
    search: {
      searchTerm: initializeSearchBarFilter(defaultValues),
      searchColumns: initializeSearchBarColumns(genericTableColumnsDefinition),
    },
    sorting: initializeSorting(defaultValues),
    pagination: {
      currentPage: initializePage(defaultValues),
      pageSize: initializeItemsPerPage(defaultValues),
    },
    select: initializeSelect(genericTableColumnsDefinition),
  };

  return query;
}

type InitializeFiltersParams = {
  genericTableColumnsDefinition: GenericTableColumnDefinitions<GenericTableSchema>;
  genericTableSchema: GenericTableSchema;
  defaultValues: URLSearchParams;
};
function initializeFilters({
  genericTableColumnsDefinition,
  defaultValues,
  genericTableSchema,
}: InitializeFiltersParams): Record<string, FilterValueType> {
  const result: Record<string, FilterValueType> = {};
  for (const key of Object.keys(genericTableColumnsDefinition)) {
    const columnDefinition = genericTableColumnsDefinition[key];
    if (!columnDefinition) {
      continue;
    }

    const { isCustomColumn, isFilterable } = columnDefinition;

    if (columnDefinition.initialFilter) {
      result[key] = columnDefinition.initialFilter;
    }

    if (isCustomColumn || isFilterable === false) {
      continue;
    }

    const paramValue = defaultValues?.get(key);
    const fieldDefinition = genericTableSchema.fields[key];
    result[key] = paramValue
      ? filterMapperFactory(mapFieldDefinitionToFilterType(fieldDefinition)).fromQuery(paramValue)
      : getDefaultFilterObject(fieldDefinition.format);
  }

  return result;
}

function initializeSearchBarFilter(defaultValues: URLSearchParams): string {
  const searchBarValue = defaultValues.get(QueryStringKeyword.SEARCH_TERM);
  if (searchBarValue) {
    return searchBarValue;
  }

  return "";
}

function initializeSearchBarColumns(genericTableColumnsDefinition: GenericTableColumnDefinitions<GenericTableSchema>): string[] {
  return Object.entries(genericTableColumnsDefinition)
    .filter(
      ([, fieldDefinition]) => fieldDefinition?.isVisible && !fieldDefinition.isCustomColumn && fieldDefinition.isSearchable,
    )
    .map(([key]) => key);
}

function initializeSorting(defaultValues: URLSearchParams) {
  const sorting: GenericTableSortingItems = [];
  const sortingList = defaultValues.getAll(QueryStringKeyword.SORTING);
  if (!sortingList) {
    return sorting;
  }

  sortingList.forEach((order) => {
    const [field, direction] = order.split("$-$");
    if (!field || !direction) {
      throw new Error(`Invalid sorting query param`);
    }

    const sortDirection = direction === SortingDirection.ASC ? SortingDirection.ASC : SortingDirection.DESC;
    sorting.push({ fieldName: field, direction: sortDirection });
  });

  return sorting;
}

function initializePage(defaultValues: URLSearchParams): number {
  const pageValue = defaultValues.get(QueryStringKeyword.PAGE);
  if (!pageValue) {
    return 1;
  }

  const page = parseInt(pageValue, 10);
  if (isNaN(page) || page < 1) {
    throw new Error(`Invalid page query param`);
  }

  return page;
}

function initializeSelect(
  genericTableColumnsDefinition: GenericTableColumnDefinitions<GenericTableSchema>,
): undefined | string[] {
  const select: string[] = [];
  for (const [key, fieldDefinition] of Object.entries(genericTableColumnsDefinition)) {
    if (fieldDefinition && !fieldDefinition.isCustomColumn) {
      select.push(key);
    }
  }

  return select.length > 0 ? select : undefined;
}

function initializeItemsPerPage(params: URLSearchParams): number {
  const itemsPerPageValue = params.get(QueryStringKeyword.PAGE_SIZE);
  if (!itemsPerPageValue) {
    return 20;
  }

  const itemsPerPage = parseInt(itemsPerPageValue, 10);
  if (isNaN(itemsPerPage) || itemsPerPage < 1) {
    throw new Error(`Invalid items per page query param`);
  }

  return itemsPerPage;
}

function getDefaultFilterObject(format: GenericTableSchemaFormat): FilterValueType {
  switch (format) {
    case GenericTableSchemaFormat.ARRAY:
      return {
        type: GenericTableFilterType.ARRAY,
        filter: { option: ArrayColumnOperators.CONTAINS_ANY, value: undefined },
      };
    case GenericTableSchemaFormat.DATE:
    case GenericTableSchemaFormat.DATE_TIME:
      return {
        type: GenericTableFilterType.DATE,
        filter: { option: DateColumnOperators.BETWEEN, value: undefined },
      };
    case GenericTableSchemaFormat.STRING:
    case GenericTableSchemaFormat.LANGUAGE_RECORD:
      return {
        type: GenericTableFilterType.STRING,
        filter: { option: StringColumnOperators.STARTS_WITH, value: undefined },
      };
    case GenericTableSchemaFormat.INTEGER:
    case GenericTableSchemaFormat.FLOAT:
    case GenericTableSchemaFormat.DECIMAL:
      return {
        type: GenericTableFilterType.NUMBER,
        filter: { option: NumberColumnOperators.EXACT, value: undefined },
      };
    case GenericTableSchemaFormat.BOOLEAN:
      return {
        type: GenericTableFilterType.BOOLEAN,
        filter: { option: BooleanColumnOperators.BOOLEAN, value: undefined },
      };
    case GenericTableSchemaFormat.JSON:
      throw new Error("Custom fields are not supported for filtering");
    default:
      assertNever(format);
  }
}

interface ToggleSortingDirectionParams {
  sorting: GenericTableSortingItems;
  fieldName: string;
}

export function toggleSortingDirection({ sorting, fieldName }: ToggleSortingDirectionParams) {
  const fieldSortingIndex = sorting.findIndex((sortingItem) => sortingItem.fieldName === fieldName);
  if (fieldSortingIndex !== -1) {
    if (sorting[fieldSortingIndex].direction === SortingDirection.ASC) {
      sorting[fieldSortingIndex].direction = SortingDirection.DESC;
    } else {
      sorting.splice(fieldSortingIndex, 1);
    }
  } else {
    sorting.push({ fieldName, direction: SortingDirection.ASC });
  }
}
